import type { FanslyApi } from './api'
import { asList, asObj, byId, num, playableMedia, str, unwrap, type Playable, type Raw } from './data'

/** A vault file picked to attach (the gateway send-* routes take these raw media ids). */
export interface PickedMedia {
  mediaId: string
  media: Playable
}

export interface VaultAlbum {
  id: string
  /** 38000 All, 1000 Posts, 5000 Messages, else a custom album (0). */
  type: number
  title: string
  count: number
  cover: Playable | null
  pos: number
  raw: Raw
}

export const SYSTEM_TITLES: Record<number, string> = { 38000: 'all', 1000: 'posts', 5000: 'messages' }

/** vault/albums → albums in Fansly's order, covers joined from aggregationData.media by lastItemId. */
export function albumsOf(payload: unknown): VaultAlbum[] {
  const u = asObj(unwrap(payload))
  const media = byId(asList(asObj(u.aggregationData).media))
  return asList(u.albums)
    .map((a) => ({
      id: str(a.id) || str(a.albumId),
      type: num(a.type),
      title: str(a.title) || str(a.name),
      count: num(a.itemCount ?? a.count ?? a.mediaCount),
      cover: playableMedia(media.get(str(a.lastItemId))),
      pos: num(a.pos),
      raw: a,
    }))
    .filter((a) => a.id)
    .sort((a, b) => (SYSTEM_TITLES[b.type] ? 1 : 0) - (SYSTEM_TITLES[a.type] ? 1 : 0) || a.pos - b.pos)
}

/** The label of an album: its title, else the system album's name. */
export function albumTitle(a: VaultAlbum, t: (k: string) => string): string {
  return a.title || (SYSTEM_TITLES[a.type] ? t(`vaultPicker.system.${SYSTEM_TITLES[a.type]}`) : t('vaultPicker.untitled'))
}

export type MediaFilter = '' | '1' | '2' | '3'

export interface VaultPage {
  items: PickedMedia[]
  /** The `before` cursor for the next page, '' when this was the last. */
  next: string
  /** Custom file names by media id (albumMedia.customFilename). */
  names: Record<string, string>
}

/**
 * One page of an album (media/vaultnew), in the album's own order
 * (`albumMedia`); the next page is `before=<last albumMedia id>`, as Fansly's
 * Vault pages it. Without `albumMedia`, the files in `media` order.
 */
export async function loadVaultPage(api: FanslyApi, albumId: string, opts: { mediaType?: MediaFilter; search?: string; before?: string; signal?: AbortSignal } = {}): Promise<VaultPage> {
  const res = asObj(
    await api.get(
      '/crm/vault/content',
      { albumId, mediaType: opts.mediaType ?? '', search: opts.search ?? '', before: opts.before || 0, after: 0 },
      opts.signal,
    ),
  )
  const media = byId(asList(res.media))
  const entries = asList(res.albumMedia)
  const items: PickedMedia[] = []
  const names: Record<string, string> = {}
  if (entries.length) {
    for (const e of entries) {
      const mediaId = str(e.mediaId) || str(e.id)
      const p = playableMedia(media.get(mediaId))
      if (p) items.push({ mediaId, media: p })
      if (str(e.customFilename)) names[mediaId] = str(e.customFilename)
    }
    return { items, next: str(entries[entries.length - 1]!.id), names }
  }
  for (const m of media.values()) {
    const p = playableMedia(m)
    if (p) items.push({ mediaId: p.mediaId || str(m.id), media: p })
  }
  const last = items[items.length - 1]
  return { items, next: last ? last.mediaId : '', names }
}

